import request from './request'

export interface ApiResponse<T = unknown> {
  code: number
  data: T
  msg: string
}

export interface Pagination {
  page: number
  page_size: number
  total: number
  total_pages: number
}

export interface IncomeLogListItem {
  id: number | string
  transaction_id?: string
  order_id?: string
  service_category?: string
  service_category_text?: string
  transaction_type?: string
  transaction_type_text?: string
  subtotal?: string | number
  subtotal_amount?: string | number | null
  subtotal_text?: string
  discount?: string | number | null
  discount_amount?: string | number | null
  discount_text?: string
  coupon?: string | number | null
  coupon_amount?: string | number | null
  coupon_text?: string
  coupon_id?: string | number
  tax?: string | number
  tax_amount?: string | number | null
  tax_text?: string
  total?: string | number
  total_amount?: string | number | null
  total_text?: string
  payment_method?: string
  payment_method_text?: string
  transaction_time?: string
  date?: string
  date_ymd?: string
  [key: string]: unknown
}

export interface IncomeLogListData {
  list: IncomeLogListItem[]
  pagination: Pagination
}

export interface IncomeLogListParams {
  page: number
  page_size: number
  search_type?: string
  keyword?: string
  service_category?: string
  transaction_type?: string
  payment_method?: string
  /** YYYY-MM-DD */
  date?: string
}

export interface IncomeLogMetaOption {
  value: string
  label: string
}

export interface IncomeLogMetaData {
  search_types?: Array<{ value: string; label: string } | string>
  service_categories?: Array<{ value: string; label: string } | string>
  transaction_types?: Array<{ value: string; label: string } | string>
  payment_methods?: Array<{ value: string; label: string } | string>
}

export interface IncomeLogDetail extends IncomeLogListItem {
  order_id?: string
}

export const DEFAULT_INCOME_SEARCH_TYPES: IncomeLogMetaOption[] = [
  { value: 'transaction_id', label: 'Transaction ID' },
  { value: 'subtotal', label: 'Subtotal' },
  { value: 'discount', label: 'Discount' },
  { value: 'coupon', label: 'Coupon' },
  { value: 'tax', label: 'Tax' },
  { value: 'total', label: 'Total' },
]

export const DEFAULT_SERVICE_CATEGORIES: IncomeLogMetaOption[] = [
  { value: 'All', label: 'All' },
  { value: 'hm_care', label: 'HM Care' },
  { value: 'add_on_service', label: 'Add-on Service' },
]

export const DEFAULT_TRANSACTION_TYPES: IncomeLogMetaOption[] = [
  { value: 'All', label: 'All' },
  { value: 'income', label: 'Income' },
  { value: 'refund', label: 'Refund' },
]

export const DEFAULT_PAYMENT_METHODS: IncomeLogMetaOption[] = [
  { value: 'All', label: 'All' },
  { value: 'Credit Card', label: 'Credit Card' },
  { value: 'Apple Pay', label: 'Apple Pay' },
  { value: 'Google Pay', label: 'Google Pay' },
  { value: 'Wechat Pay', label: 'Wechat Pay' },
]

function normalizeMetaOption(
  entry: { value: string; label: string } | string,
  fallbacks: IncomeLogMetaOption[],
): IncomeLogMetaOption | null {
  if (typeof entry === 'string') {
    const value = entry.trim()
    if (!value) return null
    const fallback = fallbacks.find((o) => o.value === value)
    return { value, label: fallback?.label ?? value }
  }
  const value = String(entry.value ?? '').trim()
  const label = String(entry.label ?? '').trim()
  if (!value) return null
  return { value, label: label || value }
}

function isAllOption(opt: IncomeLogMetaOption): boolean {
  const value = opt.value.trim().toLowerCase()
  const label = opt.label.trim().toLowerCase()
  return value === 'all' || value === '' || label === 'all'
}

function dedupeOptions(options: IncomeLogMetaOption[]): IncomeLogMetaOption[] {
  const seen = new Set<string>()
  const result: IncomeLogMetaOption[] = []
  for (const opt of options) {
    if (seen.has(opt.value)) continue
    seen.add(opt.value)
    result.push(opt)
  }
  return result
}

function buildOptions(
  raw: Array<{ value: string; label: string } | string> | undefined,
  fallbacks: IncomeLogMetaOption[],
  ensureAll = false,
): IncomeLogMetaOption[] {
  if (!Array.isArray(raw) || !raw.length) {
    return [...fallbacks]
  }
  let options = raw
    .map((entry) => normalizeMetaOption(entry, fallbacks))
    .filter((o): o is IncomeLogMetaOption => o != null)
  if (!options.length) return [...fallbacks]

  if (ensureAll) {
    options = options.filter((opt) => !isAllOption(opt))
    options = [{ value: 'All', label: 'All' }, ...options]
  }

  return dedupeOptions(options)
}

export function buildIncomeSearchTypes(meta: IncomeLogMetaData | null | undefined): IncomeLogMetaOption[] {
  return buildOptions(meta?.search_types, DEFAULT_INCOME_SEARCH_TYPES)
}

function isRefundServiceCategory(opt: IncomeLogMetaOption): boolean {
  const value = opt.value.trim().toLowerCase()
  const label = opt.label.trim().toLowerCase()
  return value === 'refund' || label === 'refund'
}

export function buildIncomeServiceCategories(
  meta: IncomeLogMetaData | null | undefined,
): IncomeLogMetaOption[] {
  return buildOptions(meta?.service_categories, DEFAULT_SERVICE_CATEGORIES, true).filter(
    (opt) => !isRefundServiceCategory(opt),
  )
}

export function buildIncomeTransactionTypes(
  meta: IncomeLogMetaData | null | undefined,
): IncomeLogMetaOption[] {
  return buildOptions(meta?.transaction_types, DEFAULT_TRANSACTION_TYPES, true)
}

export function buildIncomePaymentMethods(
  meta: IncomeLogMetaData | null | undefined,
): IncomeLogMetaOption[] {
  return buildOptions(meta?.payment_methods, DEFAULT_PAYMENT_METHODS, true)
}

export function resolveIncomeFilterLabel(
  options: IncomeLogMetaOption[],
  selectedValue: string,
): string {
  const hit = options.find((opt) => opt.value === selectedValue)
  return hit?.label ?? selectedValue
}

/** POST `/api/admin/income-log/meta` */
export async function postIncomeLogMeta(
  params: Partial<IncomeLogListParams> = {},
): Promise<ApiResponse<IncomeLogMetaData>> {
  return request.post<ApiResponse<IncomeLogMetaData>>('/api/admin/income-log/meta', params)
}

/** POST `/api/admin/income-log/list` */
export async function postIncomeLogList(
  params: IncomeLogListParams,
): Promise<ApiResponse<IncomeLogListData>> {
  return request.post<ApiResponse<IncomeLogListData>>('/api/admin/income-log/list', params)
}

/** POST `/api/admin/income-log/detail` */
export async function postIncomeLogDetail(
  id: string | number,
): Promise<ApiResponse<IncomeLogDetail>> {
  return request.post<ApiResponse<IncomeLogDetail>>('/api/admin/income-log/detail', { id })
}
